/**
 * @file repPricing.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-28
 * @description The rules of rep pricing — the reps' own pricing view (P7) and
 *              the office's Rep Pricing (P3), Cam 28 Oct 2026. Pure: the
 *              server loads the facts (services/repPricingService.js), these
 *              decide. Client source; utils/repPricing.js on the server is
 *              GENERATED from it (node scripts/sync-shared-utils.js).
 *
 *   THE REP'S PILES (P7 — "When it is confirmed it should move out of their
 *   'To Price' pile and into a 'Priced' pile, from where they can pull back
 *   to their 'To Price' pile … so long as not yet invoiced"):
 *     flagged    the rep's own note to the office is open (wherever the line is)
 *     invoiced   its order has a live invoice — read only; Flag for office
 *     to_price   sent to them, no live price from them and not confirmed — or
 *                pulled back after the office confirmed it
 *     priced     their price is with the office (waiting / held), or the
 *                office has confirmed a sale price (by any route)
 *   A rep's price is LIVE while nothing newer settles it: no sale confirmed at
 *   or after it, and the line not sent to them again since (a second check).
 *
 *   THE OFFICE'S BUCKETS (P3 — Send to reps and Asked a rep merged; the
 *   redesign proposal's D4 / D5): built on Pricing & Costing's own piles
 *   (utils/pricingCosting pileOf), so "needs a price" means one thing:
 *     to_send        pile 'needs' (no sale to propose, never asked), not taken
 *                    out of Send to reps
 *     waiting        pile 'asked' (sent, no price back)
 *     priced_by_rep  a live rep price (typed on their screen or imported),
 *                    pending or held, the sale not invoiced
 *     flagged        an open flag from a rep (counted from the flags, any line)
 *   Due to send: already-delivered lines first, then the soonest delivery.
 *   No reminders (Cam, 28 Oct 2026, D5: "By delivery, NO reminders" — the
 *   Remind mark after 5 working days is gone; chasing is done from the Sends
 *   tab when he chooses).
 *
 *   SIDE PILES on Pricing & Costing's rows: 'rep_priced' and 'rep_flagged' —
 *   a line is in them as well as in its own pile (D11: "a red flag on the
 *   line wherever it is").
 *
 *   1.1.0 (Cam, 28 Oct 2026 — N4: "allow reps to flag for standard users if
 *   they want contract generated from their prices"): a third side pile,
 *   'rep_contract' — a rep's open request for a contract at their price
 *   (row.rep_contract_request); contractAskPrice / contractAskBlock — the
 *   price a rep's request is about and why they cannot ask; the office's
 *   summary counts open requests per rep (`contract_asked`), any delivery
 *   date, as it counts flags.
 *
 *   1.2.0 (Cam, 28 Oct 2026 — the redesign's D3–D5, D9, D11: the office's
 *   Rep Pricing view): NO reminders — REMIND_WORKING_DAYS, workingDaysBetween,
 *   remindDue and every `remind` / `remind_requests` / `working_days` are
 *   gone. The buckets are worked out from the line's facts, not from
 *   Pricing & Costing's pile names (which the redesign changes): no sale
 *   price to propose and not settled → with a rep (asked) = Waiting, never
 *   asked = To send (`saleWithoutPrice`, `askedOf`). Each request carries
 *   what the view shows — its supplier(s), the cost it will be asked at
 *   (costs.view only; `costs` per line for the send window), the send it
 *   went in, the rep's prices and notes, any request for a contract, and its
 *   lines; `toSendCount` — how many requests are due to go to reps (the
 *   "Lines | Rep Pricing" switch's count).
 */

import { parsePrice, money, ukDate, repFlagText } from './pricingCosting';

export const NOTE_MAX = 1000;

export const REP_PILES = ['to_price', 'priced', 'invoiced', 'flagged'];
export const REP_PILE_LABELS = {
  to_price: 'To Price',
  priced: 'Priced',
  invoiced: 'Invoiced',
  flagged: 'Flagged for office'
};

export const OFFICE_BUCKETS = ['to_send', 'waiting', 'priced_by_rep', 'flagged'];
export const OFFICE_BUCKET_LABELS = {
  to_send: 'To send',
  waiting: 'Waiting',
  priced_by_rep: 'Priced by rep',
  flagged: 'Flagged by rep'
};

export const SIDE_PILES = ['rep_priced', 'rep_flagged', 'rep_contract'];
export const SIDE_PILE_LABELS = {
  rep_priced: 'Priced by rep',
  rep_flagged: 'Flagged by rep',
  rep_contract: 'Contract asked for'
};

export const REPLY_SOURCE_LABELS = {
  rep_view: 'on their pricing screen',
  import: 'from their spreadsheet'
};

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const round4 = (n) => Math.round((Number(n) + Number.EPSILON) * 10000) / 10000;
const time = (v) => {
  if (!v) return null;
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : null;
};
const later = (a, b) => {
  const ta = time(a);
  const tb = time(b);
  if (ta === null) return b || null;
  if (tb === null) return a || null;
  return ta >= tb ? a : b;
};

/** 'YYYY-MM-DD' in London from a Date or a timestamp; a bare day as it is. */
export function londonDay(v) {
  if (!v) return null;
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London' }).format(d);
}

/** A note typed by a rep or the office: trimmed, at most NOTE_MAX characters; '' when blank. */
export function cleanNote(raw) {
  return String(raw == null ? '' : raw).replace(/\s+$/g, '').replace(/^\s+/g, '').slice(0, NOTE_MAX);
}

/** A price a rep typed — "330", "£330.00", "330/T" — or null. */
export function parseRepPrice(raw) {
  const p = parsePrice(raw);
  return p !== null && p > 0 && p < 1e7 ? p : null;
}

/**
 * Is the rep's price the line's live answer? It is, unless a sale price was
 * confirmed at or after it, or the line was sent to the rep again since.
 * @param {{ repReply, saleConfirmed, lastSentAt }} f
 */
export function replyIsLive(f) {
  const r = f && f.repReply;
  if (!r || !(num(r.price) > 0)) return false;
  if (r.status && r.status !== 'pending' && r.status !== 'held') return false;
  const at = time(r.at);
  if (f.saleConfirmed && time(f.saleConfirmed.at) !== null && at !== null && time(f.saleConfirmed.at) >= at) return false;
  if (f.lastSentAt && at !== null && time(f.lastSentAt) > at) return false;
  return true;
}

/**
 * The rep's pile for one line.
 * @param {object} f  { invoiced, saleConfirmed { value, at, by, source },
 *                      repReply { price, at, status, source, notes } (newest
 *                      not approved), lastSentAt, openFlag, openPullBack }
 */
export function repPileOf(f) {
  if (f.openFlag) return 'flagged';
  return pileWithoutFlag(f);
}

function pileWithoutFlag(f) {
  if (f.invoiced) return 'invoiced';
  const live = replyIsLive(f);
  if (f.openPullBack && !(live && time(f.repReply.at) > time(f.openPullBack.at))) return 'to_price';
  if (live) return 'priced';
  if (f.saleConfirmed) return 'priced';
  return 'to_price';
}

/**
 * Where the line stands with the office, for the rep: { state, text }.
 *   invoiced | held | waiting | confirmed | pulled_back | to_price
 */
export function repStateOf(f) {
  if (f.invoiced) {
    const inv = f.invoiced;
    return { state: 'invoiced', text: `Invoiced${num(inv.value) > 0 ? ` at ${money(inv.value)}` : ''}${inv.invoice_no ? ` (${inv.invoice_no})` : ''}` };
  }
  const live = replyIsLive(f);
  if (f.openPullBack && !(live && time(f.repReply.at) > time(f.openPullBack.at))) {
    const was = f.saleConfirmed ? ` — the office had confirmed ${money(f.saleConfirmed.value)}` : '';
    return { state: 'pulled_back', text: `Pulled back to price again${was}; the office has been told` };
  }
  if (live) {
    return f.repReply.status === 'held'
      ? { state: 'held', text: `Your ${money(f.repReply.price)} is held by the office for review — not confirmed yet` }
      : { state: 'waiting', text: `Your ${money(f.repReply.price)} is with the office — not confirmed yet` };
  }
  if (f.saleConfirmed) {
    const c = f.saleConfirmed;
    return { state: 'confirmed', text: `Confirmed ${money(c.value)}${c.by ? ` by ${c.by}` : ''}${c.at ? ` · ${ukDate(londonDay(c.at))}` : ''}` };
  }
  return { state: 'to_price', text: 'Waiting for your price' };
}

/** Why the rep cannot type a price on this line now, or null. */
export function priceBlock(f) {
  if (f.invoiced) return `${f.invoiced.invoice_no || 'It'} is invoiced — the price can't change now. Flag it for the office instead.`;
  if (f.saleConfirmed && pileWithoutFlag(f) === 'priced' && !replyIsLive(f)) {
    return `The office has confirmed ${money(f.saleConfirmed.value)} — pull it back to To Price first.`;
  }
  return null;
}

/**
 * What pulling a line back to To Price does:
 *   { allowed: true, mode: 'withdraw' }  the rep's own price comes back (the
 *                                        office has not confirmed it)
 *   { allowed: true, mode: 'flag' }      the office confirmed a price: it
 *                                        stays on the order, and the office is
 *                                        told the rep wants to price it again
 *   { allowed: false, reason }
 */
export function pullBackPlan(f) {
  if (f.invoiced) return { allowed: false, mode: null, reason: `${f.invoiced.invoice_no || 'It'} is invoiced — flag it for the office instead.` };
  if (pileWithoutFlag(f) !== 'priced') return { allowed: false, mode: null, reason: 'It is already in To Price.' };
  if (replyIsLive(f)) return { allowed: true, mode: 'withdraw', reason: null };
  if (f.saleConfirmed) return { allowed: true, mode: 'flag', reason: null };
  return { allowed: false, mode: null, reason: 'There is no price to pull back.' };
}

/** Why the rep cannot flag this line for the office now, or null. */
export function flagBlock(f) {
  if (f.openFlag) {
    return `Already flagged${f.openFlag.at ? ` on ${ukDate(londonDay(f.openFlag.at))}` : ''} — the office hasn't dealt with it yet.`;
  }
  return null;
}

/**
 * The price a rep's contract request is about (N4): their own live price,
 * else the sale price the office confirmed. null when there is none.
 * @param {{ repReply, saleConfirmed, lastSentAt }} f
 */
export function contractAskPrice(f) {
  if (replyIsLive(f)) return num(f.repReply.price);
  if (f && f.saleConfirmed && num(f.saleConfirmed.value) > 0) return num(f.saleConfirmed.value);
  return null;
}

/**
 * Why the rep cannot ask for this line's price to be made a contract price,
 * or null. `categoryRefused` — the product cannot be a contract price from
 * here (straights by month, milk powder, additives —
 * utils/pricingCosting.contractCategoryFor).
 */
export function contractAskBlock(f, { categoryRefused = null, withPrice = false } = {}) {
  if (f && f.invoiced) return `${f.invoiced.invoice_no || 'It'} is invoiced — flag it for the office instead.`;
  if (categoryRefused) return categoryRefused;
  if (!withPrice && !(contractAskPrice(f) > 0)) return 'Give your price first — a contract is made at a price.';
  return null;
}

/** Margin £ and % on cost (Pricing & Costing's "% on cost"). */
export function marginOf(price, cost) {
  const p = num(price);
  const c = num(cost);
  if (p === null || c === null || !(c > 0) || !(p > 0)) return { margin: null, margin_pc: null };
  return { margin: round4(p - c), margin_pc: Math.round(((p - c) / c) * 1000) / 10 };
}

/** One figure when they agree, else { min, max }; null when there are none. */
export function oneOrRange(values) {
  const list = (values || []).map(num).filter(v => v !== null);
  if (!list.length) return null;
  const min = Math.min(...list);
  const max = Math.max(...list);
  return Math.abs(max - min) < 0.00005 ? { value: round4(min), min: round4(min), max: round4(min) } : { value: null, min: round4(min), max: round4(max) };
}

/**
 * A rep's lines grouped as their spreadsheet groups them — one entry per
 * request (customer › product) and pile, its lines in that pile. Pure.
 * @param {Array<{ line_id, request_key, pile }>} lines
 * @returns {Array<{ key, request_key, pile, lines }>}
 */
export function groupRepLines(lines) {
  const map = new Map();
  for (const l of lines || []) {
    const k = `${l.request_key}|${l.pile}`;
    if (!map.has(k)) map.set(k, { key: k, request_key: l.request_key, pile: l.pile, lines: [] });
    map.get(k).lines.push(l);
  }
  return [...map.values()];
}

// ---------------------------------------------------------------------------
// The office's side
// ---------------------------------------------------------------------------

/** When a Pricing & Costing row was last sent to a rep (the order line's stamp, or the send record). */
export function sentAtOf(row) {
  return later(row && row.sale && row.sale.asked ? row.sale.asked.at : null, row && row.rep_request ? row.rep_request.last_sent_at : null);
}

/** Has this line been asked of a rep (stamped, or in a recorded send)? */
export const askedOf = (row) => !!(row && ((row.sale && row.sale.asked) || row.rep_request));

/** Has the line been delivered (its status, else its delivery date before today)? */
export function deliveredOf(row, today) {
  if (row && row.status && row.status.delivered) return true;
  const d = row && row.delivery_date ? String(row.delivery_date).slice(0, 10) : null;
  return !!(d && today && d < today);
}

/**
 * The extra piles a Pricing & Costing row is in, beside its own (D11): an
 * open rep flag, and a live rep price the office has not settled.
 * @param {object} row  a Pricing & Costing row with `rep_flag`, `rep_price`
 */
export function officeSidePiles(row) {
  const out = [];
  if (!row || row.kind !== 'order_line') return out;
  if (row.rep_flag) out.push('rep_flagged');
  if (row.rep_contract_request) out.push('rep_contract');
  const s = row.sale || {};
  const rp = row.rep_price;
  if (s.applies && !s.locked && rp && replyIsLive({
    repReply: { price: rp.price, at: rp.at, status: rp.status },
    saleConfirmed: s.confirmed || null,
    lastSentAt: sentAtOf(row)
  })) out.push('rep_priced');
  return out;
}

/**
 * Is the line's sale still without a price to confirm? Not invoiced, not
 * confirmed (or reopened), and nothing to propose — the screen's own choice
 * when it has made one (`chosen`), else the server's proposal. A row that
 * carries neither falls back to its pile (needs / asked / held).
 */
export function saleWithoutPrice(row) {
  const s = (row && row.sale) || {};
  if (!s.applies || s.locked) return false;
  if (s.confirmed && !s.reopened) return false;
  if ('chosen' in s) return !s.chosen;
  if ('proposed' in s) return !s.proposed;
  return ['needs', 'asked', 'held'].includes(row.pile);
}

/**
 * The office bucket of one Pricing & Costing row (flags are counted apart),
 * or null. From the line's facts (1.2.0), so the Lines view's pile names
 * can change without moving a line here:
 *   priced_by_rep  a live rep price the office has not settled
 *   waiting        no price to confirm, asked of a rep, nothing back
 *   to_send        no price to confirm, never asked, not taken out of Send to reps
 */
export function officeBucketOf(row) {
  if (!row || row.kind !== 'order_line') return null;
  const side = row.side_piles || officeSidePiles(row);
  if (side.includes('rep_priced')) return 'priced_by_rep';
  if (!saleWithoutPrice(row)) return null;
  if (askedOf(row)) return 'waiting';
  if (row.sale && row.sale.notForReps) return null;
  return 'to_send';
}

/** A request's key: Pricing & Costing's product group (the price request's own key), else the row. */
export const requestKeyOf = (row) => (row && row.group && row.group.request) || (row && row.key) || null;

/**
 * How many requests are due to go to reps (one per rep and request) — the
 * "Lines | Rep Pricing" switch's count (D3).
 */
export function toSendCount(rows) {
  const keys = new Set();
  for (const row of rows || []) {
    if (officeBucketOf(row) !== 'to_send') continue;
    keys.add(`${(row.rep && row.rep.id) || 0}|${requestKeyOf(row)}`);
  }
  return keys.size;
}

/**
 * The cost a line will be asked at, as Pricing & Costing shows it: confirmed
 * (and not reopened), else chosen / proposed. null without costs (the row
 * then carries no cost side).
 */
export function lineCostOf(row) {
  const c = (row && row.cost) || {};
  if (!c.applies) return null;
  if (c.confirmed && !c.reopened && num(c.confirmed.value) > 0) return num(c.confirmed.value);
  const pick = 'chosen' in c ? c.chosen : c.proposed;
  return pick && num(pick.value) > 0 ? num(pick.value) : null;
}

/** Due to send: delivered first, then the soonest delivery (no date last), then customer, product. */
export function dueCompare(a, b) {
  if (!!a.delivered !== !!b.delivered) return a.delivered ? -1 : 1;
  const da = a.earliest_delivery || '9999-99-99';
  const db = b.earliest_delivery || '9999-99-99';
  if (da !== db) return da < db ? -1 : 1;
  const ca = String((a.customer && a.customer.name) || '');
  const cb = String((b.customer && b.customer.name) || '');
  if (ca !== cb) return ca.localeCompare(cb);
  return String((a.product && a.product.name) || '').localeCompare(String((b.product && b.product.name) || ''));
}

/** Waiting: the longest wait first (the oldest last send). */
function waitingCompare(a, b) {
  const ta = time(a.sent_at) ?? Infinity;
  const tb = time(b.sent_at) ?? Infinity;
  if (ta !== tb) return ta - tb;
  return dueCompare(a, b);
}

/** Priced by rep: the newest price first. */
function pricedCompare(a, b) {
  const ta = time(a.rep_price && a.rep_price.at) ?? 0;
  const tb = time(b.rep_price && b.rep_price.at) ?? 0;
  if (ta !== tb) return tb - ta;
  return dueCompare(a, b);
}

const emptyRep = (id, name, email) => ({
  rep_id: id, rep_name: name || (id ? null : 'No rep'), rep_email: email || null,
  to_send: { lines: 0, requests: 0, delivered_lines: 0, delivered_requests: 0, earliest_delivery: null },
  waiting: { lines: 0, requests: 0, oldest_sent_at: null },
  priced_by_rep: { lines: 0, requests: 0, held_lines: 0, from_rep_view_lines: 0 },
  flagged: { open: 0, pull_backs: 0 },
  contract_asked: { lines: 0, requests: 0 },
  last_send: null
});

/**
 * Rep Pricing's summary (P3): per rep, what is to send, waiting, priced by
 * the rep and flagged, the last send; and every request in a bucket, in the
 * order it is worked (due to send soonest first, delivered first). Pure.
 * @param {object[]} rows       Pricing & Costing rows (listRows)
 * @param {object} o
 * @param {string} o.today      London 'YYYY-MM-DD'
 * @param {Array<{ id, rep_id, rep_name, kind }>} [o.flags]      open flags
 * @param {Map<number, object>} [o.lastSends]  rep id → { id, at, by, how, line_count }
 * @param {Map<number, { name, email }>} [o.reps]  every rep to list even with nothing
 * @param {Array<{ rep_id, rep_name, order_line_id, account_id, item_code }>} [o.contractRequests]
 *        open requests for a contract (N4) — counted per rep as lines and as
 *        requests (one per customer and product), any delivery date
 */
export function summariseByRep(rows, { today, flags = [], lastSends = new Map(), reps = new Map(), contractRequests = [] } = {}) {
  const byRep = new Map();
  const repOf = (id, name, email) => {
    const k = id || 0;
    if (!byRep.has(k)) byRep.set(k, emptyRep(id || null, name, email));
    const r = byRep.get(k);
    if (!r.rep_name && name) r.rep_name = name;
    if (!r.rep_email && email) r.rep_email = email;
    return r;
  };
  for (const [id, r] of reps) repOf(Number(id), r.name, r.email);

  const groups = new Map();
  for (const row of rows || []) {
    const bucket = officeBucketOf(row);
    if (!bucket) continue;
    const repId = row.rep && row.rep.id ? Number(row.rep.id) : null;
    const reqKey = requestKeyOf(row);
    const k = `${bucket}|${repId || 0}|${reqKey}`;
    if (!groups.has(k)) {
      groups.set(k, {
        key: k, bucket, rep_id: repId, rep_name: (row.rep && row.rep.name) || null, request_key: reqKey,
        customer: row.customer ? { id: row.customer.id, name: row.customer.name, account_no: row.customer.account_no } : null,
        product: row.item ? { code: row.item.code, name: row.item.name, range: row.item.range || null } : null,
        additives: row.additives || [], delivery_method: row.delivery_method || null, unit: row.unit || null, per: row.per || '',
        line_ids: [], row_keys: [], orders: [], quantity: 0, suppliers: [],
        earliest_delivery: null, latest_delivery: null, delivered: false, delivered_lines: 0,
        sent_at: null, send: null, rep_price: null, rep_prices: null, notes: [], held: false,
        cost: null, costs: {}, contract_request: null, unit_flags: 0
      });
    }
    const g = groups.get(k);
    g.line_ids.push(row.order_line_id);
    g.row_keys.push(row.key);
    const ref = (row.refs || []).find(x => x.type === 'SO-D');
    const lineSent = sentAtOf(row);
    const lineCost = lineCostOf(row);
    const delivered = deliveredOf(row, today);
    g.orders.push({
      order_id: row.order_id, order_number: ref ? ref.label : null, line_id: row.order_line_id, row_key: row.key,
      delivery_date: row.delivery_date || null, quantity: num(row.qty), supplied: num(row.supplied), delivered,
      supplier: (row.supplier && row.supplier.name) || null, cost: lineCost, sent_at: lineSent,
      send_id: row.rep_request ? row.rep_request.send_id || null : null,
      rep_price: row.rep_price ? num(row.rep_price.price) : null,
      rep_contract_request: row.rep_contract_request || null
    });
    g.quantity = round4(g.quantity + (num(row.qty) || 0));
    const sup = row.supplier && row.supplier.name;
    if (sup && !g.suppliers.includes(sup)) g.suppliers.push(sup);
    if (lineCost !== null) g.costs[row.order_line_id] = lineCost;
    if (row.rep_contract_request && !g.contract_request) g.contract_request = row.rep_contract_request;
    if (Array.isArray(row.unit_flags) && row.unit_flags.length) g.unit_flags += 1;
    const d = row.delivery_date ? String(row.delivery_date).slice(0, 10) : null;
    if (d && (!g.earliest_delivery || d < g.earliest_delivery)) g.earliest_delivery = d;
    if (d && (!g.latest_delivery || d > g.latest_delivery)) g.latest_delivery = d;
    if (delivered) { g.delivered = true; g.delivered_lines += 1; }
    if (lineSent && (!g.sent_at || time(lineSent) >= time(g.sent_at))) {
      g.sent_at = lineSent;
      g.send = row.rep_request
        ? { send_id: row.rep_request.send_id || null, at: row.rep_request.last_sent_at || lineSent, by: row.rep_request.sent_by || null, how: row.rep_request.how || null, second_check: !!row.rep_request.second_check }
        : { send_id: null, at: lineSent, by: (row.sale && row.sale.asked && row.sale.asked.by) || null, how: null, second_check: false };
    }
    if (bucket === 'priced_by_rep' && row.rep_price) {
      if (!g.rep_price || time(row.rep_price.at) > time(g.rep_price.at)) g.rep_price = row.rep_price;
      if (row.rep_price.status === 'held') g.held = true;
      const note = row.rep_price.notes ? String(row.rep_price.notes).trim() : '';
      if (note && !g.notes.includes(note)) g.notes.push(note);
    }
  }

  const requests = [...groups.values()];
  for (const g of requests) {
    g.cost = oneOrRange(Object.values(g.costs));
    if (g.bucket === 'priced_by_rep') g.rep_prices = oneOrRange(g.orders.map(o => o.rep_price));
    const r = repOf(g.rep_id, g.rep_name, null);
    const lines = g.line_ids.length;
    if (g.bucket === 'to_send') {
      r.to_send.lines += lines;
      r.to_send.requests += 1;
      r.to_send.delivered_lines += g.delivered_lines;
      if (g.delivered) r.to_send.delivered_requests += 1;
      if (g.earliest_delivery && (!r.to_send.earliest_delivery || g.earliest_delivery < r.to_send.earliest_delivery)) r.to_send.earliest_delivery = g.earliest_delivery;
    } else if (g.bucket === 'waiting') {
      r.waiting.lines += lines;
      r.waiting.requests += 1;
      if (g.sent_at && (!r.waiting.oldest_sent_at || time(g.sent_at) < time(r.waiting.oldest_sent_at))) r.waiting.oldest_sent_at = g.sent_at;
    } else if (g.bucket === 'priced_by_rep') {
      r.priced_by_rep.lines += lines;
      r.priced_by_rep.requests += 1;
    }
  }
  // Held / rep-view counts are per line.
  for (const row of rows || []) {
    if (officeBucketOf(row) !== 'priced_by_rep') continue;
    const r = repOf(row.rep && row.rep.id ? Number(row.rep.id) : null, row.rep && row.rep.name, null);
    if (row.rep_price && row.rep_price.status === 'held') r.priced_by_rep.held_lines += 1;
    if (row.rep_price && row.rep_price.source === 'rep_view') r.priced_by_rep.from_rep_view_lines += 1;
  }
  for (const f of flags || []) {
    const r = repOf(f.rep_id ? Number(f.rep_id) : null, f.rep_name || null, null);
    r.flagged.open += 1;
    if (f.kind === 'pull_back') r.flagged.pull_backs += 1;
  }
  const asked = new Map();
  for (const c of contractRequests || []) {
    const r = repOf(c.rep_id ? Number(c.rep_id) : null, c.rep_name || null, null);
    r.contract_asked.lines += 1;
    const k = `${c.rep_id || 0}|${c.account_id}|${String(c.item_code || '').trim().toLowerCase()}`;
    if (!asked.has(k)) { asked.set(k, true); r.contract_asked.requests += 1; }
  }
  for (const r of byRep.values()) {
    const ls = r.rep_id ? lastSends.get(Number(r.rep_id)) : null;
    if (ls) r.last_send = ls;
  }

  const order = { to_send: 0, waiting: 1, priced_by_rep: 2 };
  requests.sort((a, b) => {
    if (a.bucket !== b.bucket) return order[a.bucket] - order[b.bucket];
    if (a.bucket === 'waiting') return waitingCompare(a, b);
    if (a.bucket === 'priced_by_rep') return pricedCompare(a, b);
    return dueCompare(a, b);
  });
  const reps2 = [...byRep.values()].sort((a, b) => {
    if (!a.rep_id !== !b.rep_id) return a.rep_id ? -1 : 1;
    return String(a.rep_name || '').localeCompare(String(b.rep_name || ''));
  });
  return { reps: reps2, requests };
}

/** A rep flag on a line, in words for the office (hover text) — Pricing & Costing's own. */
export const flagText = (flag) => repFlagText(flag);
